import { library } from '@fortawesome/fontawesome-svg-core';
import { faEdit, faUnlink } from '@fortawesome/free-solid-svg-icons';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { S_PaginationProps } from '../../../../design/pagination/S_Pagination';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { Home } from '../../../routes/config/Home';
import { ProjectStatus } from '../../projects/projects.model';
import { IDatasetDetailsProjects } from '../datasetsDetails.store';

library.add(faUnlink, faEdit);

interface IDatasetDetailsProjectsProps {
  datasetProjects: IDatasetDetailsProjects[];
  workspaceId: string;
  isLoading: boolean;
  sorting: {
    orderBy: string;
    orderType: string;
  };
  pagination: S_PaginationProps;
  onSortingChange: (orderBy: string, orderType: string) => Promise<void>;
}

export const DatasetDetailsProjectsList = observer((props: IDatasetDetailsProjectsProps): ReactElement => {
  const { t } = useTranslator();
  const gColumns = (): IGColumnProps<IDatasetDetailsProjects>[] => {
    return [
      {
        sortBy: 'name',
        headerName: t('name'),
        renderer: renderProjectName,
      },
      {
        sortBy: 'description',
        headerName: t('description'),
        renderer: (x) => x.description,
      },
      {
        sortBy: 'assignedToProjectDate',
        headerName: t('assigned_to_project'),
        renderer: (project: IDatasetDetailsProjects) => <>{toLocaleDateTimeString(project.assignedToProjectDate)}</>,
      },
      {
        sortBy: 'reviewsDonePercentage',
        headerName: t('percent_done'),
        renderer: (project: IDatasetDetailsProjects) => <>{project.reviewsDonePercentage}%</>,
      },
    ];
  };

  const renderProjectName = (projectItem: IDatasetDetailsProjects): ReactElement => {
    const name = projectItem.name ? projectItem.name : `(${t('untitled')})`;

    return (
      <S_Link2
        color='text'
        to={
          projectItem.status === ProjectStatus.Published
            ? Home.Projects.Details.Overview.withParams({ projectId: projectItem.id, workspaceId: props.workspaceId })
            : Home.Projects.Details.AnnotationView.withParams({ projectId: projectItem.id, workspaceId: props.workspaceId })
        }
      >
        {name}
      </S_Link2>
    );
  };

  return (
    <div className='tab-content'>
      <GTable<IDatasetDetailsProjects>
        isLoading={props.isLoading}
        columns={gColumns()}
        noItemsPlaceholder={t('no_project')}
        items={props.datasetProjects}
        paginationProps={props.pagination}
        sortingModel={[props.sorting]}
        onSortChanged={props.onSortingChange}
      />
    </div>
  );
});
